import React, { useContext, useEffect, useState, useCallback, memo } from "react";
import { Link } from "react-router-dom";
import { _imagesURL, FollowApi } from "../../../constants/API";
import axios from "axios";
import { toast } from "react-toastify";
import ProfileAvatar from "../../common/ProfileAvatar";
import { Language } from "../../../App";
import ProfileSuggestionModal from "../../modals/ProfileSuggestionModal";
import "../../agenda/AgendaUpcomingSidebar.css";
import "../../agenda/AgendaFoundation.css";
import { authorize } from "../../../shared/authorize";
import { MdOutlineMedicalServices } from "react-icons/md";
import { IoIosArrowForward } from "react-icons/io";
import { RiVerifiedBadgeFill } from "react-icons/ri";
import { getProfileRoute, getUserSubtitle } from "../../../utils/profileNavigator";
import "./suggestedDoctors.css";

/* ── Shimmer (matching LiveFeeds / AgendaUpcomingSidebar style) ── */
const ProfileShimmerRow = ({ nameWidth = "55%", subWidth = "40%" }) => (
  <div className="agenda-sidebar-shimmer-row" aria-hidden="true">
    {/* Circular avatar thumb */}
    <div
      className="agenda-shimmer-block"
      style={{ width: "44px", height: "44px", borderRadius: "50%", flexShrink: 0 }}
    />
    {/* Name + subtitle lines */}
    <div className="agenda-sidebar-shimmer-info">
      <span
        className="agenda-sidebar-shimmer-title agenda-shimmer-block"
        style={{ width: nameWidth }}
      />
      <span
        className="agenda-sidebar-shimmer-meta agenda-shimmer-block"
        style={{ width: subWidth }}
      />
    </div>
  </div>
);

const SuggestedProfilesShimmer = () => (
  <div className="agenda-sidebar-shimmer-container" role="status" aria-label="Loading suggestions">
    <div className="agenda-sidebar-shimmer-group">
      <div className="agenda-timeline-items">
        <ProfileShimmerRow nameWidth="58%" subWidth="42%" />
        <ProfileShimmerRow nameWidth="50%" subWidth="36%" />
        <ProfileShimmerRow nameWidth="62%" subWidth="44%" />
        <ProfileShimmerRow nameWidth="48%" subWidth="38%" />
      </div>
    </div>
  </div>
);

const SuggestedProfiles = ({ suggestions = [], updateSuggestions }) => {
  const userToken = JSON.parse(localStorage.getItem("SignupData"));
  const user_id = userToken?.data?.id;
  const { lang } = useContext(Language);
  const [suggestionsData, setSuggestionsData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [spinner, setSpinner] = useState(true);

  const toggleModal = useCallback(() => {
    setModalOpen((prev) => !prev);
  }, []);

  const handleFollow = useCallback(
    (id) => {
      setIsLoading(true);
      axios
        .get(`${FollowApi}${id}`, authorize())
        .then((res) => {
          if (res.data.status) {
            const updater = (prevSuggestions) =>
              prevSuggestions.map((profile) =>
                profile.id === id
                  ? { ...profile, follow: !profile.follow }
                  : profile
              );
            setSuggestionsData(updater);
            if (updateSuggestions) {
              updateSuggestions(updater);
            }
          } else {
            toast.error(res.data.error);
          }
        })
        .catch(console.error)
        .finally(() => {
          setIsLoading(false);
        });
    },
    [user_id, updateSuggestions]
  );

  useEffect(() => {
    if (suggestions.length === 0) {
      setSpinner(true);
    } else {
      setSuggestionsData(suggestions);
      setSpinner(false);
    }
  }, [suggestions]);

  return (
    <>
      <ProfileSuggestionModal
        preview={modalOpen}
        updatePreview={toggleModal}
        list={suggestionsData}
        updateList={(newList) => {
          setSuggestionsData(newList);
          if (updateSuggestions) updateSuggestions(newList);
        }}
        toFollow={handleFollow}
        loading={isLoading}
      />
      <div
        className="bg-white feed-card-hover d-xl-block d-none mb-3 position-relative overflow-hidden"
        style={{
          width: "335px",
          maxWidth: "335px",
          borderRadius: "24px",
          border: "1px solid #D7E0EA",
          background: "linear-gradient(180deg, #FFFFFF 0%, #F8FDFF 100%)",
          boxShadow: "0px 8px 30px rgba(0, 186, 218, 0.05), 0px 2px 8px rgba(0, 0, 0, 0.03)",
          overflowY: "auto",
          transition: "all 0.25s ease",
          fontFamily: "'Onest', sans-serif",
        }}
      >
        {/* Ambient Glow */}
        <div
          style={{
            position: "absolute",
            top: "-30px",
            right: "-30px",
            width: "120px",
            height: "120px",
            background: "#00BADA",
            opacity: 0.1,
            filter: "blur(45px)",
            borderRadius: "50%",
            pointerEvents: "none",
            zIndex: 1,
          }}
        />

        {/* Header Bar */}
        <div
          className="p-3 border-bottom d-flex justify-content-between align-items-center position-relative"
          style={{ borderColor: "#F1F5F9", zIndex: 2 }}
        >
          <div className="d-flex align-items-center gap-2">
            <div
              className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
              style={{
                width: "28px",
                height: "28px",
                background: "#E8FBFF",
                color: "#00BADA",
                border: "1px solid #BCEEFA",
              }}
            >
              <MdOutlineMedicalServices size={16} />
            </div>
            <h4 className="fs-15 roboto-medium mb-0" style={{ color: "#0F1A2B", lineHeight: 1.2, fontWeight: 600 }}>
              {lang?.people_suggestions || "Suggested Doctors"}
            </h4>
          </div>
          <span
            onClick={toggleModal}
            className="fs-12 cursor roboto-medium mb-0 hover-blue d-inline-flex align-items-center gap-1"
            style={{ color: "#00BADA", transition: "all 0.2s ease", fontWeight: 600 }}
          >
            {lang?.view_all || "View All"} <IoIosArrowForward size={13} />
          </span>
        </div>

        {/* Doctors List */}
        <div className="py-2 position-relative" style={{ zIndex: 2 }}>
          {spinner ? (
            <SuggestedProfilesShimmer />
          ) : (
            <>
              {suggestionsData.slice(0, 5).map((value, index) => (
                <React.Fragment key={value.id}>
                  <div className="d-flex px-3 py-2.5 justify-content-between align-items-center feed-row-hover">
                    <Link
                      to={getProfileRoute(value)}
                      className="d-flex align-items-center text-decoration-none text-dark flex-grow-1"
                      style={{ gap: "12px", minWidth: 0 }}
                    >
                      <div className="position-relative flex-shrink-0">
                        <ProfileAvatar
                          src={value.image}
                          size={44}
                          variant="subtle"
                          alt={value.name || "Profile"}
                        />
                        <div
                          style={{
                            position: "absolute",
                            bottom: "-2px",
                            right: "-2px",
                            width: "16px",
                            height: "16px",
                            borderRadius: "50%",
                            background: "#FFFFFF",
                            color: "#00BADA",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "11px",
                            boxShadow: "0 1px 4px rgba(0, 186, 218, 0.3)",
                          }}
                        >
                          <RiVerifiedBadgeFill />
                        </div>
                      </div>
                      <div className="d-flex flex-column flex-grow-1" style={{ minWidth: 0 }}>
                        <p
                          className="fs-14 roboto-medium mb-0 text-truncate hover-blue"
                          style={{ color: "#0F1A2B", lineHeight: 1.3, fontWeight: 600 }}
                          title={value?.name}
                        >
                          {value?.name}
                        </p>
                        <span
                          className="fs-12 roboto-regular text-truncate mt-0.5"
                          style={{ color: "#5F6673" }}
                          title={getUserSubtitle(value, lang)}
                        >
                          {getUserSubtitle(value, lang)}
                        </span>
                      </div>
                    </Link>
                    <div className="align-self-center flex-shrink-0 ms-2">
                      {String(value.id) !== String(user_id) && (
                        <button
                          disabled={isLoading}
                          className={`sd-follow-btn ${
                            value.follow ? "sd-follow-btn--following" : "sd-follow-btn--follow"
                          }`}
                          style={{
                            padding: "4.5px 13px",
                            fontSize: "11.5px",
                            width: "auto",
                            whiteSpace: "nowrap",
                          }}
                          onClick={() => handleFollow(value.id)}
                        >
                          {value.follow ? lang?.following || "Following" : `+ ${lang?.follow || "Follow"}`}
                        </button>
                      )}
                    </div>
                  </div>
                  {index !== suggestionsData.slice(0, 5).length - 1 && (
                    <hr className="my-1 mx-3" style={{ borderColor: "#F1F5F9", opacity: 0.6 }} />
                  )}
                </React.Fragment>
              ))}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default memo(SuggestedProfiles);
